const Product = {

  /* Maintains the current state of the product, this will update itself as the user takes action */
  state: {
    variants: null,
    selectedVariant: null,
    lastSelections: {
      option1: null,
      option2: null,
      option3: null
    },
    handle: null,
    thumbnailContainer: null,
    activeImage: null,
    activeImageIndex: 0
  },

  /**
   * Maintains the user selection and sets the hidden variant ID in the ATC form
   * @param {Element} selector - The selector that triggered this function
   */
  updateSelection: function (selector) {
    this.state.lastSelections[selector.dataset.option] = selector.value
    let possibleVariants = this.state.variants.filter(variant => {
      return (
        variant.option1 == this.state.lastSelections.option1
        && variant.option2 == this.state.lastSelections.option2
        && variant.option3 == this.state.lastSelections.option3
      )
    })

    // When only one variant matches user selections, that must be the selected variant
    if (possibleVariants.length == 1) {
      let variantField = document.getElementById('pdp-variant-id')
      let addToCartButtons = document.querySelectorAll('[data-pdp-button-add]')

      this.state.selectedVariant = possibleVariants[0]
      variantField.value = this.state.selectedVariant.id
      for (const button of addToCartButtons) {
        if (this.state.selectedVariant.available) {
          button.disabled = false
          button.classList.remove('disabled');
          button.removeAttribute('aria-disabled')
          button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M376 232H216V72c0-4.42-3.58-8-8-8h-32c-4.42 0-8 3.58-8 8v160H8c-4.42 0-8 3.58-8 8v32c0 4.42 3.58 8 8 8h160v160c0 4.42 3.58 8 8 8h32c4.42 0 8-3.58 8-8V280h160c4.42 0 8-3.58 8-8v-32c0-4.42-3.58-8-8-8z"/></svg> <span class="ps-2 fs-6">Add to Bag</span>'
        } else {
          button.disabled = true
          button.classList.add('disabled');
          button.setAttribute('aria-disabled', true)
          button.innerHTML = 'Sold out'
        }
      }
    }
  },

  /**
   * Returns a JSON representation of the passed product URL.
   * @param {String} productUrl - The URL for the product you want the data from. Does not need ?view parameter. Can be /products/path
   */
  getData: async function (productUrl) {
    let thisProductData = null
    await fetch(`${productUrl}?view=jsononly`)
      .then(productResponse => productResponse.json())
      .then(jsonResponse => {
        thisProductData = jsonResponse
      })
    return thisProductData
  },

  /**
   * Sets the src of the main image to the src of the passed imageElement, and sets height of thumbnail container
   * @param {Element} imageElement - The DOM element of the new image (thumbnail)
   */
  setActiveImage: function (imageElement) {
    // Set featured image src - State active image does not change (same element), but active image index will
    this.state.activeImage.src = imageElement.src
    this.state.activeImageIndex = imageElement.dataset.index

    // Set thumbnail viewer height to match new featured image height if viewport width > 767px or auto if viewport width <= 767px
    let viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0)
    if (viewportWidth > 767) {
      let imageHeight = this.state.activeImage.offsetHeight
      this.state.thumbnailContainer.style.height = imageHeight + "px"
    } else {
      this.state.thumbnailContainer.style.height = "auto"
    }
  },

  /**
   * Scrolls the image thumbnail container
   * @param {String} direction - Direction to scroll the container. Valid values are "up" and "down". TO DO: "left" and "right"
   */
  scrollThumbnails(direction) {
    let container = this.state.thumbnailContainer
    let allThumbnails = Array.from(container.querySelectorAll(".product-thumbnail"))

    // Check if we should scroll straight back to the top
    if (direction == "down" && container.scrollTop == (container.scrollHeight - container.offsetHeight)) {
      container.scroll({
        left: 0,
        top: 0,
        behavior: "smooth"
      })
      return
    }

    // Check if we should scroll straight back to the bottom
    if (direction == "up" && container.scrollTop <= (allThumbnails[0].offsetTop - container.offsetTop)) {
      container.scroll({
        left: 0,
        top: container.scrollHeight,
        behavior: "smooth"
      })
      return
    }

    /**
     * Scrolls down the thumbnail list
     *    Finds the first instance of the top of a thumbnail being within the thumbnail containers current viewport
     *    Scrolls to the end of that thumbnail in the list
     */
    if (direction == "down") {
      for (let thumbnail of allThumbnails) {
        let thumbnailTop = thumbnail.offsetTop - container.offsetTop
        if (thumbnailTop >= container.scrollTop) {
          container.scroll({
            left: 0,
            top: thumbnail.offsetHeight + thumbnailTop - 40,
            behavior: "smooth",
          })
          break
        }
      }

      /**
       * Scrolls up the thumbnail list
       *    Finds the last instance of the top of a thumbnail being outside the thumbnail containers current viewport
       *    Scrolls to the start of that thumbnail in the list
       */
    } else if (direction == "up") {
      let reversedThumbnails = allThumbnails.reverse()
      for (let thumbnail of reversedThumbnails) {
        let thumbnailTop = thumbnail.offsetTop - container.offsetTop
        if (thumbnailTop < container.scrollTop) {
          container.scroll({
            left: 0,
            top: thumbnailTop - 40,
            behavior: "smooth"
          })
          break
        }
      }
    }
  },

  /**
   * Initializes a product page
   *    Sets the state variants of this product
   *    Sets state handle of this product
   *    Adds event listeners to all option selectors
   *        The event listener updates the user selection
   *        For swatches, the event listener also displays the active selection
   *    Sets up image gallery and thumbnail container
   *    Setups up product tabs (if necessary)
   *    Adds this product to users recently viewed products.
   * @param {String} productPath - The path to the product e.g. /products/a-blue-dress
   */
  initialize: async function (productPath) {
    // Set state variants
    await fetch(`${productPath}?view=jsononly`)
      .then(productResponse => productResponse.json())
      .then(jsonResponse => {
        this.state.variants = jsonResponse.variants
      })

    // Set state handle
    this.state.handle = productPath.split("/").slice(-1).pop()

    // Sets event listeners on option selectors
    let optionSelectors = Array.from(document.querySelectorAll('.pdp-option-select'))
    for (let optionSelector of optionSelectors) {
      optionSelector.addEventListener("change", function () {
        if (this.type == "radio") {
          let thisInput = this
          let thisLabel = this.closest('label')
          let thisOptionContainer = thisLabel.closest('[data-pdp-option]')
          let thisSelectedOptionPlaceholder = thisOptionContainer.querySelector('[data-pdp-option-selected-text]') || false
          let thisOptionSiblings = Array.from(thisOptionContainer.querySelectorAll("label"))
          for (let optionSiblingLabel of thisOptionSiblings) {
            optionSiblingLabel.classList.remove("is-active")
          }
          thisLabel.classList.add("is-active")
          if (thisSelectedOptionPlaceholder) thisSelectedOptionPlaceholder.innerHTML = thisInput.value
        }
        Product.updateSelection(this)
      })
    }


    // custom option selected text
    let customOptions = document.querySelectorAll('[data-pdp-option-custom-input][type=radio], [data-pdp-option-custom-input][type=checkbox]')
    for (let option of customOptions) {
      option.addEventListener('change', function (e) {
        let currInput = this;
        if (currInput.checked) {
          let scope = currInput.closest('[data-pdp-option-custom]')
          let propertyValuePlaceholder = scope.querySelector('[data-pdp-option-custom-selected-text]')
          propertyValuePlaceholder.innerHTML = currInput.value
        }
      })
    }

    // Sets up image gallery
    if (document.getElementById("ProductThumbnailContainer")) {
      // Add click event to all thumbnails to change main image
      this.state.thumbnailContainer = document.getElementById("ProductThumbnailContainer")
      let allThumbnails = Array.from(this.state.thumbnailContainer.querySelectorAll(".product-thumbnail"))
      for (let thumbnail of allThumbnails) {
        thumbnail.addEventListener("click", function () {
          Product.setActiveImage(this)
        })
      }

      // Set the initial activeImage state and then run setActiveImage to scale the thumbnail container
      this.state.activeImage = document.getElementById("ProductActiveImage")
      this.setActiveImage(this.state.activeImage)

      // Run setActiveImage on resize to rescale thumbnail container
      window.addEventListener('resize', function () {
        Product.setActiveImage(Product.state.activeImage)
      });
    }

    // Sets up product tab headers
    if (document.querySelector(".tabbed-headers")) {
      let tabHeaders = Array.from(document.querySelectorAll(".tabbed-headers label"))
      for (let thisHeader of tabHeaders) {
        thisHeader.addEventListener("click", function () {
          for (let header of tabHeaders) {
            header.classList.remove("active", "text-primary")
          }
          thisHeader.classList.add("active", "text-primary")
        })
      }
    }
  }

}

const recommendationTabs = () => {
  const tabItems = document.querySelectorAll('[data-tabitem]');
  const tabContent = document.querySelectorAll('[data-tab]');
  document.addEventListener('click', e => {
    if (e.target.matches('[data-tabitem]')) {
      const currentTab = e.target.dataset.tabitem;
      const currentContent = document.querySelector(`[data-tab="${currentTab}"`);
      // Set tab state //
      tabItems.forEach(item => {
        item.dataset.state = "inactive";
      });
      e.target.dataset.state = "active"
      // Set tab content state
      tabContent.forEach(content => {
        content.dataset.state = "inactive";
      });
      currentContent.dataset.state = "active";
    }
  });
}

window.Product = Product
document.addEventListener("DOMContentLoaded", function () {
  if (ShopifyAnalytics && ShopifyAnalytics.meta.page.resourceType == "product") {
    let productPath = window.location.pathname
    Product.initialize(productPath)
    recommendationTabs();
  }
})


export default Product